import React, { useEffect, useRef, useState } from 'react';
import { LuCloud, LuLaptop, LuCheck, LuX, LuRadar, LuLoader, LuInfo } from 'react-icons/lu';

export const EXECUTION_MODES = {
  SERVER: 'server',
  LOCAL: 'local'
};

const MODE_OPTIONS = [
  {
    value: EXECUTION_MODES.SERVER,
    title: 'Server',
    icon: LuCloud,
    badge: 'Recommended',
    description: 'Crawl on our cloud infrastructure. Best for publicly reachable applications.',
    highlights: ['No local setup required', 'Runs in the background']
  },
  {
    value: EXECUTION_MODES.LOCAL,
    title: 'Local',
    icon: LuLaptop,
    description: 'Crawl from your own machine. Best for apps on localhost, VPN or private networks.',
    highlights: ['Access internal environments', 'Requires the local agent running']
  }
];

/**
 * ExecutionModeModal
 * Asks the user where UI discovery should crawl the application (server or local)
 * before the discovery run is started.
 *
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Called when the modal is dismissed
 * - onConfirm: function(mode) - Called with the selected execution mode
 * - isLoading: boolean - Shows loading state while discovery is starting
 */
const ExecutionModeModal = ({ isOpen, onClose, onConfirm, isLoading = false }) => {
  const [selectedMode, setSelectedMode] = useState(EXECUTION_MODES.LOCAL);
  const dialogRef = useRef(null);

  // Reset to default and focus the dialog each time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setSelectedMode(EXECUTION_MODES.LOCAL);
    dialogRef.current?.focus();
  }, [isOpen]);

  // Escape to close, lock background scroll
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) onClose();
  };

  const handleOptionKeyDown = (e) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const currentIndex = MODE_OPTIONS.findIndex(o => o.value === selectedMode);
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + step + MODE_OPTIONS.length) % MODE_OPTIONS.length;
    setSelectedMode(MODE_OPTIONS[nextIndex].value);
    e.currentTarget.parentElement?.children[nextIndex]?.focus();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[var(--modal-backdrop)] backdrop-blur-sm p-0 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="execution-mode-title"
        tabIndex={-1}
        className="w-full sm:max-w-2xl max-h-[92vh] flex flex-col bg-[var(--surface)] border border-[var(--border)] shadow-2xl outline-none rounded-t-2xl sm:rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 sm:pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--primary-muted)] text-[var(--primary-color)]">
              <LuRadar size={20} />
            </div>
            <div>
              <h2 id="execution-mode-title" className="text-lg font-semibold text-[color:var(--text-primary)] leading-tight">
                Where should we crawl your application?
              </h2>
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">
                Choose the environment that will run the UI discovery crawler.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
            className="rounded-lg p-1.5 text-[color:var(--text-subtle)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[color:var(--text-secondary)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <LuX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5">
          <div
            role="radiogroup"
            aria-label="Execution mode"
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
          >
            {MODE_OPTIONS.map(option => {
              const Icon = option.icon;
              const isSelected = selectedMode === option.value;

              return (
                <div
                  key={option.value}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                  onClick={() => !isLoading && setSelectedMode(option.value)}
                  onKeyDown={handleOptionKeyDown}
                  className={`relative flex cursor-pointer flex-col rounded-xl border-2 p-4 sm:p-5 text-left transition-all duration-200 outline-none focus-visible:ring-4 focus-visible:ring-[var(--primary-muted)] ${
                    isSelected
                      ? 'border-[var(--primary-color)] bg-[var(--primary-subtle)] shadow-md'
                      : 'border-[var(--border)] bg-[var(--surface)] hover:border-[var(--border-strong)] hover:shadow-sm'
                  } ${isLoading ? 'pointer-events-none opacity-70' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-lg transition-colors ${
                        isSelected ? 'bg-[var(--primary-color)] text-white' : 'bg-[var(--surface-hover)] text-[color:var(--text-secondary)]'
                      }`}
                    >
                      <Icon size={22} />
                    </div>
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${
                        isSelected ? 'border-[var(--primary-color)] bg-[var(--primary-color)] text-white' : 'border-[var(--border-strong)]'
                      }`}
                    >
                      {isSelected && <LuCheck size={12} strokeWidth={3} />}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold text-[color:var(--text-primary)]">{option.title}</span>
                    {option.badge && (
                      <span className="rounded-full bg-[var(--success-muted)] px-2 py-0.5 text-xs font-medium text-[color:var(--success)] ring-1 ring-[color-mix(in_srgb,var(--success)_35%,transparent)]">
                        {option.badge}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-[color:var(--text-muted)]">{option.description}</p>

                  <ul className="mt-3 space-y-1.5">
                    {option.highlights.map(item => (
                      <li key={item} className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
                        <LuCheck size={14} className={isSelected ? 'text-[var(--primary-color)]' : 'text-[color:var(--text-subtle)]'} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {selectedMode === EXECUTION_MODES.LOCAL && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-[color-mix(in_srgb,var(--warning)_35%,transparent)] bg-[var(--warning-muted)] p-3 text-sm text-[color:var(--warning)]">
              <LuInfo size={18} className="mt-0.5 shrink-0" />
              <span>Make sure the local agent is installed and running on your machine before starting discovery.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 border-t border-[var(--border-subtle)] bg-[var(--bg-tertiary)] px-5 sm:px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto rounded-lg border border-[var(--border)] bg-[var(--surface)] px-5 py-2.5 text-sm font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(selectedMode)}
            disabled={isLoading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-110 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? <LuLoader size={18} className="animate-spin" /> : <LuRadar size={18} />}
            {isLoading ? 'Starting Discovery...' : 'Start UI Discovery'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutionModeModal;
